<script lang="ts" setup>
const props = defineProps<{
  data: {
    id: string
    indicator: string
    image: string
    title: string
    description: string
  }
}>()

const emit = defineEmits(['activated'])

const { $gsap, $scrollTrigger } = useNuxtApp()

const itemRef = ref<HTMLElement>()

const active = ref(false)
watch(active, (isActive) => {
  if (isActive)
    emit('activated')
})

const scrollTrigger = ref<ScrollTrigger | null>(null)

onMounted(() => {
  scrollTrigger.value = $scrollTrigger.create({
    // markers: true,
    start: 'top 50%',
    end: 'bottom 50%',
    trigger: itemRef.value!,
    onToggle: ({ isActive }) => {
      active.value = isActive
    },
  })
  $gsap.from(itemRef.value!, {
    scrollTrigger: scrollTrigger.value,
  })
})

const { top } = useElementBounding(itemRef)

const position = computed(() => {
  return top.value + window.scrollY
})

watch(position, () => {
  if (!scrollTrigger.value)
    return
  scrollTrigger.value.refresh()
})
</script>

<template>
  <div ref="itemRef" class="relative py-2rem">
    <div
      class="relative h-25rem w-full lg:h-45rem"
    >
      <div
        class="relative h-full w-full transform-gpu transition duration-600 ease-in-out"
        :class="{
          'opacity-20 lg:scale-60 scale-90': !active,
          'opacity-100 scale-100': active,
        }"
      >
        <NuxtImg
          loading="lazy"
          provider="directus" :src="props.data.image"
          height="450"
          class="h-full w-full object-cover"
        />
        <div class="gradient pointer-events-none absolute inset-0" />
      </div>
      <h1
        class="text-neutal-white absolute inset-x-0 top-0 transform-gpu text-center text-balance text-4rem font-title transition duration-600 ease-in-out lg:inset-x-none lg:top-6rem lg:max-w-75% lg:text-left lg:text-6rem lg:-left-15%"
        :class="{
          'opacity-0 translate-y-6rem lg:-translate-x-24rem lg:translate-y-0': !active,
          'opacity-100 ': active,
        }"
      >
        {{ props.data.title }}
      </h1>
      <div
        class="absolute bottom-0 hidden transform-gpu text-2rem text-neutral-gray font-title duration-600 ease-in-out -left-15% lg:block"
        :class="{
          'opacity-0 -translate-x-2rem scale-150': !active,
          'opacity-100 ': active,
        }"
      >
        {{ props.data.indicator }}
      </div>
    </div>
    <div
      class="transform-gpu pt-2rem text-2rem text-neutral-gray font-title duration-600 ease-in-out lg:hidden"
      :class="{
        'opacity-0 -translate-y-6rem': !active,
        'opacity-100 ': active,
      }"
    >
      {{ props.data.indicator }}
    </div>
    <p
      class="whitespace-pre-line pt-2rem text-1.6rem text-neutral-white transition duration-600 ease-in-out lg:text-justify"
      :class="{
        'opacity-0 -translate-y-12rem': !active,
        'opacity-100': active,
      }"
    >
      {{ props.data.description }}
    </p>
  </div>
</template>

<style scoped>
.gradient {
  background: linear-gradient(180deg, rgba(0, 6, 15, 0.69) 0%, rgba(0, 6, 15, 0) 63%);
}
</style>
